import { Inject, Injectable, WritableSignal, signal } from '@angular/core';
import { timer } from 'rxjs';
import { SystemsRepositoryPort } from 'common/systems/application/ports/systems-repository.port';
import { SystemsRealtimePort } from 'common/systems/application/ports/systems-realtime.port';
import { SystemsStatePort } from 'common/systems/application/ports/systems-state.port';
import { LoadSystemsUseCase } from 'common/systems/application/use-cases/load-systems.use-case';
import { INITIAL_SYSTEMS_STATE, SystemsState } from 'common/systems/domain/entities/systems-state';
import { mergeSystemUpdate } from 'common/systems/domain/merge-system-update';
import { selectAll, selectById } from 'common/systems/domain/selectors/systems.selectors';
import { System } from 'common/systems/domain/resources/system.resource';
import { SYSTEMS_POLLING_INTERVAL_MS } from 'ui/features/systems/infrastructure/tokens/systems-polling-interval.token';

/**
 * Angular, signal-based store for the `systems` capability. It is the sole
 * owner of the live `SystemsState` (mirroring `DealsBagStore` owning
 * `ActiveDealsBagState`), the only point of this capability that creates a
 * polling `timer(...)`, and the only one that subscribes to
 * `SystemsRealtimePort`. It depends directly on `SystemsRepositoryPort`
 * and `SystemsRealtimePort` — never on `SystemsFacade` — to avoid a
 * circular dependency with `SystemsStatePort`'s implementation
 * (`SystemsStoreAdapter`), which wraps this store (see spec.md of
 * `systems-capability`, Requirement "`SystemsStore` in Angular as the
 * sole owner of `SystemsState`, polling, and the realtime subscription").
 */
@Injectable({ providedIn: 'root' })
export class SystemsStore implements SystemsStatePort {
    private readonly stateSignal: WritableSignal<SystemsState> = signal(INITIAL_SYSTEMS_STATE);

    private readonly loadSystemsUseCase: LoadSystemsUseCase;

    public constructor(
        private readonly systemsRepositoryPort: SystemsRepositoryPort,
        private readonly systemsRealtimePort: SystemsRealtimePort,
        @Inject(SYSTEMS_POLLING_INTERVAL_MS) private readonly pollingIntervalMs: number,
    ) {
        // Built directly to avoid the Angular provider cycle documented in systems-capability Decision 10.
        this.loadSystemsUseCase = new LoadSystemsUseCase(this.systemsRepositoryPort, this);
        this.loadOnce();
        this.subscribeToRealtimeUpdates();
        this.subscribeToPolling();
    }

    public getAll(): ReadonlyArray<System> {
        return selectAll(this.stateSignal());
    }

    public getById(id: string): System | undefined {
        return selectById(this.stateSignal(), id);
    }

    public applyUpdate(updatedSystems: ReadonlyArray<System>): void {
        this.mergeIntoOwnState(updatedSystems);
    }

    private loadOnce(): void {
        this.loadSystemsUseCase.execute().subscribe();
    }

    private subscribeToRealtimeUpdates(): void {
        this.systemsRealtimePort
            .onSystemsUpdated()
            .subscribe((updatedSystems: ReadonlyArray<System>): void =>
                this.mergeIntoOwnState(updatedSystems),
            );
    }

    private subscribeToPolling(): void {
        timer(this.pollingIntervalMs, this.pollingIntervalMs).subscribe((): void => {
            this.loadSystemsUseCase.execute().subscribe();
        });
    }

    private mergeIntoOwnState(updatedSystems: ReadonlyArray<System>): void {
        this.stateSignal.update(
            (currentState: SystemsState): SystemsState =>
                mergeSystemUpdate(currentState, updatedSystems),
        );
    }
}
